import { useQuery } from '@apollo/client';
import mapboxgl from 'mapbox-gl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useCurrentUser } from '@/contexts/CurrentUserContext';
import { MAPBOX_ACCESS_TOKEN } from '../../../utils/constants';
import { getOtherDistanceUnits, useDistanceUnits } from '../../../utils/distanceUnits';
import Legend from '../../Legend';
import { ChangeUnitsButton } from '../ChangeUnitsButton';
import { geojson, linestring } from '../map.constants';
import { CLICKABLE_COUNTRY_QUERY, GET_MAP_CONFIG_QUERY } from '../operations';
import { RulerButton } from '../RulerButton';
import Settings from '../Settings';
import { MAP_TILESET_TYPES } from '../Utils/constants';
import { DARK_MAP_STYLE } from '../Utils/mapStyleTypes';
import { rulerEvent } from '../Utils/utils';
import { createDynamicMap } from './createDynamicMap';

const DynamicMap = ({
  mapContainerId = 'mapContainer',
  setMap,
  style = DARK_MAP_STYLE.value,
  setStyle = () => {},
  layers = [],
  layerFilters = [],
  layerStyle,
  hideLayers = [],
  enableEntityHighlight,
  enableInfoPopup,
  layerClickCallback,
  legendProps = {},
  control,
  ruler,
  styles = {},
  settingsProps = {},
  syncMap,
  callbacks = {},
  countryClickProps = {},
  removeLayerCallback = () => {},
  defaultMapPosition,
  tilesetType = MAP_TILESET_TYPES.oilAndGasMap,
}) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const mapPosition = useParams();
  const [mapLoaded, setMapLoaded] = useState(false);
  const [rulerActive, setRulerActive] = useState(false);
  const { distanceUnits, setDistanceUnits } = useDistanceUnits();
  const { currentUser } = useCurrentUser();

  const { data } = useQuery(CLICKABLE_COUNTRY_QUERY, {
    variables: {
      filters: countryClickProps.filters,
      selectedCountry: countryClickProps.selectedCountry,
    },
    skip: !countryClickProps.enabled,
  });

  const { data: mapConfigData, loading: mapConfigLoading } = useQuery(GET_MAP_CONFIG_QUERY, {
    cacheTime: 120000,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: suppress dependency of map vars
  useEffect(() => {
    if (mapContainer.current && !map.current && !mapConfigLoading) {
      const defaultMapCenter = defaultMapPosition?.latLong
        ? JSON.parse(defaultMapPosition?.latLong)
        : null;
      const paramsMapCenter = mapPosition?.latLong ? JSON.parse(mapPosition?.latLong) : null;
      const mapCenter = defaultMapCenter || paramsMapCenter || [0, 0];
      const mapZoom = defaultMapPosition?.zoom || mapPosition?.zoom || 2;

      mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
      const enableCountryClick = !!countryClickProps.enabled;
      const newMap = createDynamicMap({
        container: mapContainerId,
        style,
        center: mapCenter,
        zoom: mapZoom,
        ruler,
        control,
        enableInfoPopup,
        enableCountryClick,
        layerClickCallback,
        layerFilters,
        layers,
        hideLayers,
        enableEntityHighlight,
        callbacks,
        tilesetType,
        defaultDistanceMetric: distanceUnits,
        initialMetric: layerStyle?.metric,
        initialSizeType: layerStyle?.sizeType,
        source: {
          url: mapConfigData?.mapboxConfig?.mapboxSourceUrl,
          token: currentUser.jwtToken,
        },
      });
      map.current = newMap;
      if (setMap) setMap(newMap);
      newMap.on('load', () => setMapLoaded(true));
    }
    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [
    mapContainerId,
    defaultMapPosition,
    tilesetType,
    layerStyle,
    countryClickProps.enabled,
    mapConfigData,
  ]);

  // Handle country click data
  useEffect(() => {
    if (mapLoaded && data && data.mapInteractiveCountries?.feature && map.current?.layerCallbacks) {
      map.current.layerCallbacks.setCountryBoundaryStyle(data.mapInteractiveCountries.feature);
    }
  }, [data, mapLoaded]);

  // Handle layer style changes
  useEffect(() => {
    if (mapLoaded && layerStyle && map.current && map.current.layerCallbacks) {
      const { layer, metric, sizeType } = layerStyle;
      if (map.current.layerCallbacks.changeLayerStyle) {
        map.current.layerCallbacks.changeLayerStyle(layer, metric, sizeType);
      }
    }
  }, [layerStyle, mapLoaded]);

  // Handle layer filter changes
  useEffect(() => {
    if (mapLoaded && layerFilters && map.current && map.current.layerCallbacks) {
      if (map.current.layerCallbacks.filterLayers) {
        map.current.layerCallbacks.filterLayers(layerFilters);
      }
    }
  }, [layerFilters, mapLoaded]);

  // biome-ignore lint/correctness/useExhaustiveDependencies(map): suppress dependency map
  const onDistanceUnitsChange = useCallback(() => {
    const newMetric = getOtherDistanceUnits(distanceUnits);
    setDistanceUnits(newMetric);
    map.current._scaleControl?.setUnit(newMetric);
    map.current.onClickRulerCallback = rulerEvent(map.current, geojson, linestring, newMetric);
  }, [map, distanceUnits, setDistanceUnits]);

  // prepend legend to mapbox controls container if legend is enabled or we want to keep the legend mounted
  const setLegend = useCallback(
    node => {
      if (mapLoaded && node) {
        const controlContainer = document.getElementsByClassName('mapboxgl-control-container')[0];
        const bottomRightControlContainer = document.getElementsByClassName(
          'mapboxgl-ctrl-bottom-right',
        )[0];
        if (controlContainer && bottomRightControlContainer) {
          controlContainer.insertBefore(node, bottomRightControlContainer);
        }
      }
    },
    [mapLoaded],
  );

  // Layer control functions for Settings panel
  const addLayer = useCallback(
    (layer, style) => {
      if (map.current?.layerCallbacks?.[layer.call]) {
        map.current.layerCallbacks[layer.call](style);
        if (syncMap?.layerCallbacks?.[layer.call]) {
          syncMap.layerCallbacks[layer.call](style);
        }
      }
    },
    [syncMap],
  );

  const removeLayer = useCallback(
    layer => {
      if (map.current?.layerCallbacks?.[layer.call]) {
        map.current.layerCallbacks[layer.call]();
        removeLayerCallback();
        if (syncMap?.layerCallbacks?.[layer.call]) {
          syncMap.layerCallbacks[layer.call]();
        }
      }
    },
    [removeLayerCallback, syncMap],
  );

  const toggleLayer = useCallback(
    (layer, visibility) => {
      if (map.current?.layerCallbacks?.[layer.call]) {
        map.current.layerCallbacks[layer.call](layer.entityTypes, visibility);
        if (syncMap?.layerCallbacks?.[layer.call]) {
          syncMap.layerCallbacks[layer.call](layer.entityTypes, visibility);
        }
      }
    },
    [syncMap],
  );

  const toggleMapStyle = useCallback(
    newStyle => {
      setMapLoaded(false);
      if (map.current?.toggleMapStyle) {
        map.current.toggleMapStyle(newStyle);
        // Set map as loaded again after style change
        map.current.once('style.load', () => {
          setMapLoaded(true);
        });
      }
      if (syncMap?.toggleMapStyle) {
        syncMap.toggleMapStyle(newStyle);
      }
      setStyle(newStyle);
    },
    [setStyle, syncMap],
  );

  return (
    <>
      <div
        ref={mapContainer}
        id={mapContainerId}
        style={{ width: '100%', height: '100%', ...styles }}
      />
      {/* Legend panel */}
      <div style={{ visibility: 'hidden', position: 'absolute', top: 0, left: 0 }}>
        <div ref={legendProps.enabled || legendProps.keepMounted ? setLegend : () => {}}>
          {(legendProps.enabled || legendProps.keepMounted) && (
            <Legend styles={styles} multiline title {...legendProps} />
          )}
        </div>
        {ruler && (
          <RulerButton
            rulerActive={rulerActive}
            setRulerActive={setRulerActive}
            map={map.current}
            mapLoaded={mapLoaded}
            distanceUnits={distanceUnits}
          />
        )}
        {ruler && (
          <ChangeUnitsButton
            rulerActive={rulerActive}
            mapLoaded={mapLoaded}
            distanceUnits={distanceUnits}
            onDistanceUnitsChange={onDistanceUnitsChange}
          />
        )}
      </div>
      {/* biome-ignore lint/correctness/useUniqueElementIds: Required for mapbox initialization */}
      <div id='distanceContainer' />
      {/* Settings panel */}
      {settingsProps.enabled && (
        <Settings
          {...settingsProps}
          layerProps={{ ...settingsProps.layerProps, mapLoaded }}
          legendContainerRef={!(legendProps.enabled || legendProps.keepMounted) ? setLegend : null}
          addLayer={addLayer}
          mapStyle={style}
          removeLayer={removeLayer}
          toggleLayer={toggleLayer}
          toggleMapStyle={toggleMapStyle}
        />
      )}
    </>
  );
};

export default DynamicMap;
